// @ts-nocheck
/**
 * PDP Sticky Add-to-Cart (desktop)
 *
 * Mirrors the real PDP section (`<section class="product-section" data-testid="pdp-product-detail">`):
 *   - Title, price, compare-at price
 *   - Selected color swatch (dot + label)
 *   - Popover with all frames (option-button groups) and color swatches,
 *     optionally grouped by frame when the variant-picker uses grouped swatches
 *   - Add-to-Cart label + disabled state
 *
 * All interactions forward to the real inputs/buttons inside `<variant-picker>` / the
 * product form so the existing PDPProductDetail logic continues to own price,
 * availability, and cart submission.
 *
 * Scroll behavior:
 *   - Visible once the main .product-add-to-cart button scrolls above the viewport
 *   - Hidden again once the entire .product-section has scrolled above the viewport
 */
(function () {
  function findPdp(sticky) {
    // Prefer explicit section-id pairing (legacy inline use)
    const sectionId = sticky.dataset.sectionId;
    if (sectionId) {
      const esc = CSS.escape(sectionId);
      const byId = document.querySelector(
        `[data-testid="pdp-product-detail"][data-section-id="${esc}"], [data-testid="pdp-scrambler-detail"][data-section-id="${esc}"]`
      );
      if (byId) return byId;
    }
    // Fallback: match by product handle if provided by the standalone section
    const handle = sticky.dataset.productHandle;
    if (handle) {
      const byHandle = Array.from(
        document.querySelectorAll('[data-testid="pdp-product-detail"], [data-testid="pdp-scrambler-detail"]')
      ).find((el) => {
        const h = el.dataset.productHandle;
        return !h || h === handle;
      });
      if (byHandle) return byHandle;
    }
    // Last resort: first PDP on the page
    return document.querySelector('[data-testid="pdp-product-detail"], [data-testid="pdp-scrambler-detail"]');
  }

  function init(sticky) {
    const pdp = findPdp(sticky);
    if (!pdp) return;

    const pdpForm = pdp.querySelector('.product-form');
    const pdpAtc = pdp.querySelector('.product-add-to-cart');
    const pdpPricing = pdp.querySelector('.product-pricing');
    const variantPicker = pdp.querySelector('variant-picker');
    if (!pdpForm || !pdpAtc) return;

    const stickyPrice = sticky.querySelector('.pdp-sticky-atc__price');
    const stickyCompare = sticky.querySelector('.pdp-sticky-atc__compare');
    const stickySavings = sticky.querySelector('.pdp-sticky-atc__savings');
    const stickyDot = sticky.querySelector('.pdp-sticky-atc__swatch-dot');
    const stickyLabel = sticky.querySelector('.pdp-sticky-atc__variant-label');
    const stickyToggle = sticky.querySelector('.pdp-sticky-atc__variant-toggle');
    const stickyPopover = sticky.querySelector('.pdp-sticky-atc__popover');
    const stickyGroupsHost = sticky.querySelector('.pdp-sticky-atc__popover-groups');
    const stickyCta = sticky.querySelector('.pdp-sticky-atc__cta');

    // ---- Price formatting (cents -> $1,699.00)
    function formatMoney(cents) {
      const n = Number(cents);
      if (!isFinite(n)) return '';
      return '$' + (n / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    // Sum of all checked upgrade add-on prices, in cents
    function computeUpgradeCents() {
      let total = 0;
      pdp.querySelectorAll('.upgrade-item-checkbox:checked').forEach((cb) => {
        const v = parseInt(cb.dataset.itemPriceValue, 10);
        if (!isNaN(v)) total += v;
      });
      return total;
    }

    // ---- Price
    function syncPrice() {
      if (!pdpPricing) return;

      const saleEl =
        pdpPricing.querySelector('.price--sale') ||
        pdpPricing.querySelector('.price--current');
      const originalEl = pdpPricing.querySelector('.price--original:not(.price--with-upgrades)');

      const basePrice = parseInt(
        (saleEl && saleEl.dataset.price) ||
          pdpPricing.dataset.basePrice ||
          '',
        10
      );
      const compareAt = parseInt(
        (originalEl && originalEl.dataset.comparePrice) || '',
        10
      );
      const upgradeTotal = computeUpgradeCents();

      // Sticky always shows what the user will pay = base/sale price + upgrades
      if (!isNaN(basePrice)) {
        const finalPrice = basePrice + upgradeTotal;
        stickyPrice.textContent = formatMoney(finalPrice);
      } else if (saleEl) {
        stickyPrice.textContent = (saleEl.textContent || '').trim();
      }

      // Compare-at: show only if we have a valid compare-at price above the base
      if (!isNaN(compareAt) && !isNaN(basePrice) && compareAt > basePrice) {
        // When upgrades are added, shift the compare-at by the same amount so the
        // discount stays accurate ("was $X, now $Y")
        const compareWithUpgrades = compareAt + upgradeTotal;
        stickyCompare.textContent = formatMoney(compareWithUpgrades);
        stickyCompare.hidden = false;
      } else {
        stickyCompare.hidden = true;
      }

      // Savings: sale discount only (upgrades aren't "savings")
      if (stickySavings) {
        if (!isNaN(compareAt) && !isNaN(basePrice) && compareAt > basePrice) {
          const savings = compareAt - basePrice;
          stickySavings.textContent = 'Save ' + formatMoney(savings);
          stickySavings.hidden = false;
        } else {
          stickySavings.hidden = true;
        }
      }
    }

    // ---- ATC mirror
    function syncCta() {
      stickyCta.disabled = pdpAtc.disabled;
      const mainLabel = (pdpAtc.textContent || '').trim();
      if (mainLabel) {
        stickyCta.textContent = mainLabel;
      } else {
        stickyCta.textContent = pdpAtc.disabled
          ? stickyCta.dataset.soldOutText || 'Sold Out'
          : stickyCta.dataset.addText || 'Add to Cart';
      }
    }

    // ---- Currently selected color: read the checked input inside the Color fieldset
    function findSelectedColorInput() {
      if (!variantPicker) return null;
      // Color fieldset is the one containing .product-colors or .product-colors-grouped
      const colorFieldset =
        variantPicker.querySelector('fieldset.product-option-group--grouped-swatches') ||
        Array.from(variantPicker.querySelectorAll('fieldset.product-option-group')).find((fs) =>
          fs.querySelector('.product-colors')
        );
      if (!colorFieldset) return null;
      return colorFieldset.querySelector('input[type="radio"]:checked');
    }

    function syncSelectedColor() {
      const input = findSelectedColorInput();
      if (!input) return;
      const label = input.closest('label');
      const swatchSpan = label ? label.querySelector('.color-swatch') : null;
      if (swatchSpan) {
        stickyDot.style.background = swatchSpan.style.background || '#1a1a1a';
      }
      stickyLabel.textContent = input.value || '';
    }

    // ---- Popover: build from variant-picker structure
    function buildPopover() {
      if (!variantPicker) return;
      stickyGroupsHost.innerHTML = '';

      const groupedFieldset = variantPicker.querySelector(
        'fieldset.product-option-group--grouped-swatches'
      );
      const allFieldsets = Array.from(
        variantPicker.querySelectorAll('fieldset.product-option-group')
      );
      const nonColorPillFieldsets = allFieldsets.filter(
        (fs) =>
          !fs.classList.contains('product-option-group--grouped-swatches') &&
          !fs.querySelector('.product-colors') &&
          fs.querySelector('.option-button-label')
      );

      if (groupedFieldset) {
        // One swatch row per group (e.g. Hardtail / Full Suspension)
        groupedFieldset.querySelectorAll('.product-colors-group').forEach((grpEl) => {
          const label = grpEl.querySelector('.product-colors-group-label');
          const inputs = Array.from(grpEl.querySelectorAll('input[type="radio"]'));
          if (!inputs.length) return;
          stickyGroupsHost.appendChild(
            renderSwatchGroup((label && label.textContent.trim()) || '', inputs)
          );
        });
      } else {
        // Ungrouped: single color row
        const colorFieldset = allFieldsets.find((fs) => fs.querySelector('.product-colors'));
        if (colorFieldset) {
          const inputs = Array.from(colorFieldset.querySelectorAll('input[type="radio"]'));
          const legend = colorFieldset.querySelector('legend');
          stickyGroupsHost.appendChild(
            renderSwatchGroup((legend && legend.textContent.trim()) || 'Color', inputs)
          );
        }
      }

      // Render every non-color option fieldset as pill buttons (Battery, Size,
      // Frame when not grouped, etc). When color swatches are grouped by
      // Frame, Frame is implicit so we hide it from the popover.
      nonColorPillFieldsets.forEach((fs) => {
        if (groupedFieldset) {
          // Skip the option that's used as the swatch grouping (the option
          // whose values appear as group labels above each swatch row).
          const groupLabels = Array.from(
            groupedFieldset.querySelectorAll('.product-colors-group-label')
          ).map((el) => el.textContent.trim().toLowerCase());
          const fsValues = Array.from(fs.querySelectorAll('input[type="radio"]')).map((r) =>
            (r.value || '').trim().toLowerCase()
          );
          const isGroupingOption = fsValues.length && fsValues.every((v) => groupLabels.includes(v));
          if (isGroupingOption) return;
        }
        const legend = fs.querySelector('legend');
        const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) return;
        stickyGroupsHost.appendChild(
          renderPillGroup((legend && legend.textContent.trim()) || '', inputs)
        );
      });
    }

    function renderSwatchGroup(labelText, inputs) {
      const group = document.createElement('div');
      group.className = 'pdp-sticky-atc__popover-group';
      const h = document.createElement('div');
      h.className = 'pdp-sticky-atc__popover-label';
      h.textContent = labelText;
      group.appendChild(h);
      const row = document.createElement('div');
      row.className = 'pdp-sticky-atc__popover-swatches';
      inputs.forEach((srcInput) => {
        const swatchSpan = srcInput.parentElement
          ? srcInput.parentElement.querySelector('.color-swatch')
          : null;
        const bg = swatchSpan ? swatchSpan.style.background : '';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-sticky-atc__popover-swatch';
        btn.style.background = bg;
        btn.title = srcInput.value || '';
        if (srcInput.checked) btn.classList.add('is-selected');
        const available =
          srcInput.dataset.optionAvailable !== undefined
            ? srcInput.dataset.optionAvailable === 'true'
            : !srcInput.disabled;
        if (!available) btn.classList.add('is-unavailable');
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-unavailable')) return;
          srcInput.checked = true;
          srcInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
        row.appendChild(btn);
      });
      group.appendChild(row);
      return group;
    }

    function renderPillGroup(labelText, inputs) {
      const group = document.createElement('div');
      group.className = 'pdp-sticky-atc__popover-group';
      const h = document.createElement('div');
      h.className = 'pdp-sticky-atc__popover-label';
      h.textContent = labelText;
      group.appendChild(h);
      const row = document.createElement('div');
      row.className = 'pdp-sticky-atc__popover-pills';
      inputs.forEach((srcInput) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-sticky-atc__popover-pill';
        btn.textContent = srcInput.value || '';
        if (srcInput.checked) btn.classList.add('is-active');
        const available =
          srcInput.dataset.optionAvailable !== undefined
            ? srcInput.dataset.optionAvailable === 'true'
            : !srcInput.disabled;
        if (!available) btn.classList.add('is-unavailable');
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-unavailable')) return;
          srcInput.checked = true;
          srcInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
        row.appendChild(btn);
      });
      group.appendChild(row);
      return group;
    }

    // ---- Popover open/close (guard: variant picker/popover may not exist
    // when this section appears on a product without a variant picker)
    if (stickyToggle && stickyPopover) {
      stickyToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = stickyPopover.classList.toggle('is-open');
        stickyToggle.setAttribute('aria-expanded', String(open));
      });
    }
    document.addEventListener('click', (e) => {
      if (!sticky.contains(e.target)) {
        stickyPopover.classList.remove('is-open');
        stickyToggle.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        stickyPopover.classList.remove('is-open');
        stickyToggle.setAttribute('aria-expanded', 'false');
      }
    });

    // ---- ATC click: submit the real form (PDPProductDetail handles it)
    stickyCta.addEventListener('click', () => {
      if (stickyCta.disabled) return;
      if (typeof pdpForm.requestSubmit === 'function') pdpForm.requestSubmit();
      else pdpAtc.click();
    });

    // ---- Keep everything in sync
    const resync = () => {
      syncPrice();
      syncCta();
      syncSelectedColor();
      buildPopover();
    };

    const mo = new MutationObserver(resync);
    if (pdpPricing) {
      mo.observe(pdpPricing, { subtree: true, childList: true, characterData: true, attributes: true });
    }
    mo.observe(pdpAtc, { childList: true, characterData: true, attributes: true });
    if (variantPicker) {
      mo.observe(variantPicker, { subtree: true, attributes: true, childList: true });
      variantPicker.addEventListener('variant-update', resync);
      variantPicker.addEventListener('change', resync);
    }

    // Re-sync price when upgrade add-ons are toggled
    pdp.addEventListener('change', (e) => {
      if (e.target && e.target.classList && e.target.classList.contains('upgrade-item-checkbox')) {
        syncPrice();
      }
    });

    // ---- Visibility on scroll
    // Show once the main ATC scrolls above the viewport, and stay visible
    // for the rest of the page (through video, reviews, etc.).
    function updateVisibility() {
      const atcRect = pdpAtc.getBoundingClientRect();
      const shouldShow = atcRect.bottom < 0;

      if (shouldShow) {
        if (sticky.style.display !== 'block') sticky.style.display = 'block';
        requestAnimationFrame(() => sticky.classList.add('is-visible'));
      } else {
        sticky.classList.remove('is-visible');
        clearTimeout(sticky._hideT);
        sticky._hideT = setTimeout(() => {
          if (!sticky.classList.contains('is-visible')) sticky.style.display = 'none';
        }, 400);
      }
    }

    resync();
    updateVisibility();

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          updateVisibility();
          ticking = false;
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', updateVisibility);
  }

  function boot() {
    document.querySelectorAll('.pdp-sticky-atc').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
